import { Base } from '../../abstract/base.model';
import { AssignmentStatus } from './assignment-status.model';
import { AssignmentType } from './assignment-type.model';
import { LeaseStatus } from './lease-status.model';
import { isRecord } from '../utils/is-record';
import { dateOrNull, nullableString } from '../utils/wire-value-utils';

/**
 * An assignment of an ADN drone to an organization, built from the hub's assignment JSON
 * (hub `data-models/drone-assignment.ts`). Read-only in admin: assignments change through the
 * assign / return / revoke actions, never by sending this model back.
 */
export class DroneAssignment extends Base {
  droneId?: string | null;
  organizationId?: string | null;
  assignedToProfileId?: string | null;
  assignedByProfileId?: string | null;
  assignedAt: Date | null;
  returnedAt: Date | null;
  returnedByProfileId?: string | null;
  revokedAt: Date | null;
  revokedByProfileId?: string | null;
  revocationReason?: string | null;
  status?: AssignmentStatus | null;
  notes?: string | null;

  // Lease (ADN-2208). The dates are Postgres `date` columns, kept as the hub's string: format them
  // with `formatCalendarDate`, never `new Date()`, or US timezones show the previous day.
  assignmentType?: AssignmentType | null;
  leaseStatus?: LeaseStatus | null;
  leaseStart?: string | null;
  leaseEnd?: string | null;
  leaseTermination?: string | null;
  leaseNotes?: string | null;

  // Joined display fields
  droneName?: string | null;
  droneSerialNumber?: string | null;
  organizationName?: string | null;
  assignedByName?: string | null;
  returnedByName?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.droneId = nullableString(source['droneId']);
    this.organizationId = nullableString(source['organizationId']);
    this.assignedToProfileId = nullableString(source['assignedToProfileId']);
    this.assignedByProfileId = nullableString(source['assignedByProfileId']);
    this.assignedAt = dateOrNull(source['assignedAt']);
    this.returnedAt = dateOrNull(source['returnedAt']);
    this.returnedByProfileId = nullableString(source['returnedByProfileId']);
    this.revokedAt = dateOrNull(source['revokedAt']);
    this.revokedByProfileId = nullableString(source['revokedByProfileId']);
    this.revocationReason = nullableString(source['revocationReason']);
    this.status = nullableString(source['status']) as AssignmentStatus | null | undefined;
    this.notes = nullableString(source['notes']);
    this.assignmentType = nullableString(source['assignmentType']) as AssignmentType | null | undefined;
    this.leaseStatus = nullableString(source['leaseStatus']) as LeaseStatus | null | undefined;
    this.leaseStart = nullableString(source['leaseStart']);
    this.leaseEnd = nullableString(source['leaseEnd']);
    this.leaseTermination = nullableString(source['leaseTermination']);
    this.leaseNotes = nullableString(source['leaseNotes']);
    this.droneName = nullableString(source['droneName']);
    this.droneSerialNumber = nullableString(source['droneSerialNumber']);
    this.organizationName = nullableString(source['organizationName']);
    this.assignedByName = nullableString(source['assignedByName']);
    this.returnedByName = nullableString(source['returnedByName']);
  }
}
